import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import AuthProvider, { useAuth } from './provider/auth.provider';
import NotificationProvider from './provider/notification.provider';
import ConfirmProvider from './provider/confirm.provider';
import LoginPage from './pages/auth/login';
import MainLayout from './layouts/authenticated';
import Dashboard from './pages/dashboard/dashboard';
import { UsersManagement } from './pages/users';
import { RolesManagement } from './pages/roles';
import { StateManagement as CircleManagement } from './pages/state_circle';
import { StateManagement } from './pages/states';
import { ActivitiesManagement } from './pages/activities';
import { ClientManagement } from './pages/client';
import { ProjectManagement } from './pages/projects';
import UserProfile from './pages/profile';
import { DesignationsManagement } from './pages/designation';
import { DepartmentsManagement } from './pages/departments';
import { ClientProjectsManagement } from './pages/client_project';
import ViewProject from './pages/projects/view';
import { SitesManagement } from './pages/sites';
import ViewSite from './pages/sites/view';
import CreateSite from './pages/sites/create';
import SiteAssignment from './pages/sites/assignment';
import { VendorManagement } from './pages/vendor';
import BulkSiteUpload from './pages/sites/upload';
import CabHiringClaim from './pages/expense/cab_hiring';
import GeneralClaim from './pages/expense/general';
import ProjectClaim from './pages/expense/project';
import CabHiringView from './pages/expense/cab_hiring/view';
import GeneralClaimView from './pages/expense/general/view';
import ProjectClaimView from './pages/expense/project/view';
import AdvanceView from './pages/expense/advance/view';
import AdvancesClaim from './pages/expense/advance';
import AdvanceRestrictions from './pages/expense/advance/restrictions';
import SupplierPaymentPage from './pages/expense/supplier_payment';
import SupplierPaymentView from './pages/expense/supplier_payment/view';
import SupplierPaymentRestrictions from './pages/expense/supplier_payment/restrictions';
import { BillingsManagement } from './pages/billing';
import ViewUser from './pages/users/view';
import { AddBillingPage } from './pages/billing/add';
import AttendanceList from './pages/attendance/list';
import MyAttendancesPage from './pages/attendance/my';
import ProjectTeamAttendancesPage from './pages/attendance/project-team';
import AttendanceWorkSchedules from './pages/attendance/work-schedules';
import ProjectTeamMembersPage from './pages/attendance/project-team-members';
import AllUsersAttendancePage from './pages/attendance/all-users';
import AttendanceVerifications from './pages/attendance/verifications';
import ManualMarkSettingsPage from './pages/attendance/manual-mark-settings';
import HolidayManagement from './pages/calendar';
import LeaveManagement from './pages/leaves';
import LeaveDashboard from './pages/leaves/dashboard';
import BillingSites from './pages/billing/sites.';
import ClientView from './pages/client/view';
import VendorView from './pages/vendor/view';
import ViewBilling from './pages/billing/view';
import NotificationManagement from './pages/notification/notification';
import TemplateManagement from './pages/notification/template';
import NotificationsList from './pages/notifications/list';
import EditSite from './pages/sites/edit';
import { TrainingRequestsManagement } from './pages/training_management/requests';
import TrainingRequestView from './pages/training_management/requests/view';
import SlideshowPage from './pages/sites/slideshow';
import { TrainingInstitutesManagement } from './pages/training_management/institutes';
import { TrainingTypesManagement } from './pages/training_management/types_module';
import { TrainingPaymentsManagement } from './pages/training_management/payments';
import { TrainingCertificationsManagement } from './pages/training_management/certifications';
import RaiseTicketPage from './pages/tickets/raise';
import TicketActionsPage from './pages/tickets/manage';
import HrUserDetailsPage from './pages/hr/user-details';
import UserRegistrationPage from './pages/hr/user-registration';
import ProjectClaimReport from './pages/reports/claims/project';
import CabHiringClaimReport from './pages/reports/claims/cab-hiring';
import GeneralClaimReport from './pages/reports/claims/general';
import AdvanceClaimReport from './pages/reports/claims/advance';
import SiteReport from './pages/reports/site';
import SiteDistanceReport from './pages/reports/site-distance';
import UsersReport from './pages/reports/users';
import DailyTravelReport from './pages/reports/daily-travel';
import UserFeedbackReport from './pages/reports/user-feedback';
import SurveyWorkflowReport from './pages/reports/survey-workflow';
import SiteFwStartReport from './pages/reports/site-fw-start';
import ClientViewReport from './pages/reports/client-view';
import SiteLocationReport from './pages/reports/site-location';
import ReportingCheckinReport from './pages/reports/reporting-checkin';
import UserProductivityReport from './pages/reports/user-productivity';
import ReportsPage from './pages/reports';
import LoginReport from './pages/reports/login';
import PasswordReport from './pages/reports/password';
import PermissionReportPage from './pages/reports/permission-report';
import ProjectSitesPage from './pages/sites/project_sites_page';
import RequestedSitesPage from './pages/sites/requested_sites_page';
import MySitesPage from './pages/sites/my_sites_page';
import IncSitesPage from './pages/inc/inc_sites_page';
import MyIncSitesPage from './pages/inc/my_inc_sites_page';
import CreateIncSite from './pages/inc/create';
import EditIncSite from './pages/inc/edit';
import IncBulkUpload from './pages/inc/upload';
import ViewIncSite from './pages/inc/view';
import IncDashboard from './pages/inc/dashboard';
import DesignSitesPage from './pages/designing/design_sites_page';
import MyDesignSitesPage from './pages/designing/my_design_sites_page';
import CreateDesignSite from './pages/designing/create';
import EditDesignSite from './pages/designing/edit';
import DesignBulkUpload from './pages/designing/upload';
import DesignSiteView from './pages/designing/view';
import MastersPage from './pages/masters';
import { AppVersionManagement } from './pages/app_version';
import MapViewPage from './pages/map-view'
import VendorPortalSites from './pages/vendor_portal/sites'
import VendorPortalSiteView from './pages/vendor_portal/sites/view'
import VendorPortalTeam from './pages/vendor_portal/team'
import VendorPortalBillings from './pages/vendor_portal/billings'
import VendorPortalEligibleBillingSites from './pages/vendor_portal/billings/eligible'
import VendorPortalFieldWorkSites from './pages/vendor_portal/field_work_sites'
import VendorPortalReportSites from './pages/vendor_portal/report_sites';
import VendorPortalCompleteSites from './pages/vendor_portal/complete_sites'
import VendorPortalInvoices from './pages/vendor_portal/invoices'
import VendorPortalSiteInvoices from './pages/vendor_portal/site_invoices'
import VendorPortalSubmittedInvoices from './pages/vendor_portal/submitted_invoices'
import VendorWorkAcceptance from './pages/billing/vendor-work-acceptance'
import VendorInvoiceAcceptance from './pages/billing/vendor-invoice-acceptance'
import POIssuedPage from './pages/billing/po-issued'
import VendorInvoiceItemsPage from './pages/billing/vendor-invoice-items'
import RequirementsManagement from './pages/requirements';
import ProjectSpecificPage from './pages/requirements/project-specific';
import ActivityWidePage from './pages/requirements/activity-wide';
import CommonRequirementsPage from './pages/requirements/common';
import WarehousesPage from './pages/materials/warehouses';
import ItemsPage from './pages/materials/items';
import ClassificationCategoriesPage from './pages/materials/classification-categories';
import ClassificationSubCategoriesPage from './pages/materials/classification-subcategories';
import UnitsPage from './pages/materials/units';
import CostCategoriesPage from './pages/materials/cost-categories';
import MaterialRequestsPage from './pages/materials/requests';
import MaterialArrangementsPage from './pages/materials/arrangements';
import MaterialIssuesPage from './pages/materials/issues';
import MaterialConsumptionsPage from './pages/materials/consumptions';
import MaterialScrapsPage from './pages/materials/scraps';
import MaterialOpeningBalancesPage from './pages/materials/opening-balances';
import MaterialRecoPage from './pages/materials/reco';
import MaterialStockPage from './pages/materials/stock';
import MaterialSuppliersPage from './pages/materials/suppliers';
import MaterialTransportersPage from './pages/materials/transporters';
import MaterialManagementHub from './pages/materials/index';

// Unauthorized access component
const UnauthorizedAccess = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <Helmet>
      <title>Access Denied</title>
      <meta name="description" content="You don't have permission to access this page" />
    </Helmet>
    <div className="max-w-md w-full bg-white shadow-lg rounded p-8 text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
      <p className="text-sm text-gray-500 mb-6">
        You don't have permission to access this page. Please contact your administrator if you believe this is an error.
      </p>
      <button
        onClick={() => window.history.back()}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
      >
        Go Back
      </button>
    </div>
  </div>
);

/**
 * Every report permission there is.
 *
 * The Reports hub is a directory of the reports the caller can open, so it is
 * gated on holding at least one of them — without this it was reachable by any
 * authenticated user and simply rendered "no access to any report sections".
 * Keep in step with `reportCards` in pages/reports/index.tsx.
 */
const REPORT_PERMISSIONS = [
  { resource: 'reports', action: 'view_project_claim_report' },
  { resource: 'reports', action: 'view_cab_hiring_claim_report' },
  { resource: 'reports', action: 'view_general_claim_report' },
  { resource: 'reports', action: 'view_advance_claim_report' },
  { resource: 'reports', action: 'view_site_report' },
  { resource: 'reports', action: 'view_site_distance_report' },
  { resource: 'reports', action: 'view_daily_travel_report' },
  { resource: 'reports', action: 'view_survey_workflow_report' },
  { resource: 'reports', action: 'view_users_report' },
  { resource: 'reports', action: 'view_login_report' },
  { resource: 'reports', action: 'view_password_report' },
  { resource: 'reports', action: 'view_permission_report' },
  { resource: 'reports', action: 'view_client_view_report' },
  { resource: 'reports', action: 'view_site_location_report' },
  { resource: 'reports', action: 'view_site_fw_start_report' },
  { resource: 'reports', action: 'view_reporting_checkin_report' },
  { resource: 'reports', action: 'view_user_productivity_report' },
  { resource: 'reports', action: 'view_site_distance_report_without_km' },
];

// Protected route wrapper with permission checking
const ProtectedRoute = ({
  children,
  requiredPermission,
  requiredAnyPermissions
}: {
  children: React.ReactNode;
  requiredPermission?: { resource: string; action: string };
  requiredAnyPermissions?: Array<{ resource: string; action: string }>;
}) => {
  const { token, hasPermission, canAccessRoute } = useAuth();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // If specific permission is required, check it
  if (requiredPermission) {
    const hasRequiredPermission = hasPermission(
      requiredPermission.resource,
      requiredPermission.action
    );

    if (!hasRequiredPermission) {
      return <UnauthorizedAccess />;
    }
  }

  if (requiredAnyPermissions?.length) {
    const hasAnyRequiredPermission = requiredAnyPermissions.some((permission) =>
      hasPermission(permission.resource, permission.action)
    );

    if (!hasAnyRequiredPermission) {
      return <UnauthorizedAccess />;
    }
  }

  return <MainLayout>{children}</MainLayout>;
};

// Route-based permission wrapper
const RouteProtectedWrapper = ({
  children,
  path
}: {
  children: React.ReactNode;
  path: string;
}) => {
  const { token, canAccessRoute } = useAuth();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessRoute(path)) {
    return <UnauthorizedAccess />;
  }

  return <MainLayout>{children}</MainLayout>;
};

const AttendanceRouteRedirect = () => {
  const { hasPermission } = useAuth();

  if (hasPermission('attendance', 'view_team') || hasPermission('attendance', 'view_all')) {
    return <Navigate to="/attendance/team" replace />;
  }

  if (hasPermission('attendance', 'view_project_team') || hasPermission('attendance', 'view_all')) {
    return <Navigate to="/attendance/project-team" replace />;
  }

  return <Navigate to="/attendance/my" replace />;
};

const App = () => {
  const { token } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const titleSuffix = "";
    const sanitizedTitle = document.title
      .replace(titleSuffix, "")
      .replace(/^View Site$/, "Site ID")
      .replace(/^View Site$/, "Site ID");

    if (sanitizedTitle !== document.title) {
      document.title = sanitizedTitle;
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/login" element={token ? <Navigate to="/" /> : (
        <>
          <Helmet>
            <title>Login</title>
            <meta name="description" content="Login to access the Eminence Softech Pvt Ltd" />
          </Helmet>
          <LoginPage />
        </>
      )} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/materials" element={<Navigate to="/masters" replace />} />
      <Route path="/materials/warehouses" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialWarehouse', action: 'view_all' }}><WarehousesPage /></ProtectedRoute>} />
      <Route path="/materials/items" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialItem', action: 'view_all' }}><ItemsPage /></ProtectedRoute>} />
      <Route path="/materials/classification-categories" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialClassificationCategory', action: 'view_all' }}><ClassificationCategoriesPage /></ProtectedRoute>} />
      <Route path="/materials/classification-subcategories" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialClassificationSubCategory', action: 'view_all' }}><ClassificationSubCategoriesPage /></ProtectedRoute>} />
      <Route path="/materials/units" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialUnit', action: 'view_all' }}><UnitsPage /></ProtectedRoute>} />
      <Route path="/materials/cost-categories" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialCostCategory', action: 'view_all' }}><CostCategoriesPage /></ProtectedRoute>} />
      {/* Fulfilment queue for material raised on INC sites. */}
      {/* Material Management. Every one of these lists is scoped to the caller's
          projects on the server; the permission below only decides who may open
          the screen at all. */}
      <Route path="/materials/hub" element={<ProtectedRoute><MaterialManagementHub /></ProtectedRoute>} />
      <Route path="/materials/suppliers" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialSupplier', action: 'view_all' }}><MaterialSuppliersPage /></ProtectedRoute>} />
      <Route path="/materials/transporters" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialTransporter', action: 'view_all' }}><MaterialTransportersPage /></ProtectedRoute>} />
      <Route path="/materials/requests" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialRequest', action: 'view_all' }}><MaterialRequestsPage /></ProtectedRoute>} />
      <Route path="/materials/arrangements" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialArrangement', action: 'view_all' }}><MaterialArrangementsPage /></ProtectedRoute>} />
      <Route path="/materials/issues" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialIssue', action: 'view_all' }}><MaterialIssuesPage /></ProtectedRoute>} />
      <Route path="/materials/consumptions" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialConsumption', action: 'view_all' }}><MaterialConsumptionsPage /></ProtectedRoute>} />
      <Route path="/materials/scraps" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialScrap', action: 'view_all' }}><MaterialScrapsPage /></ProtectedRoute>} />
      <Route path="/materials/opening-balances" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialOpeningBalance', action: 'view_all' }}><MaterialOpeningBalancesPage /></ProtectedRoute>} />
      <Route path="/materials/stock" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialInventory', action: 'view_balances' }}><MaterialStockPage /></ProtectedRoute>} />
      <Route path="/materials/reco" element={<ProtectedRoute requiredPermission={{ resource: 'MaterialReco', action: 'view' }}><MaterialRecoPage /></ProtectedRoute>} />

      {/* Dashboard - Generally accessible to all authenticated users */}
      <Route
        path="/dashboard"
        element={
          <RouteProtectedWrapper path="/dashboard">
            <Helmet>
              <title>Dashboard</title>
              <meta name="description" content="View your dashboard and analytics" />
            </Helmet>
            <Dashboard />
          </RouteProtectedWrapper>
        }
      />

      <Route
        path="/profile"
        element={
          <RouteProtectedWrapper path="/profile">
            <Helmet>
              <title>User Profile</title>
              <meta name="description" content="View and edit your user profile" />
            </Helmet>
            <UserProfile />
          </RouteProtectedWrapper>
        }
      />

      {/* Team Management - Requires Team view_all permission */}
      <Route
        path="/team"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Team', action: 'view_all' }}>
            <Helmet>
              <title>Team Management</title>
              <meta name="description" content="Manage your team members and their information" />
            </Helmet>
            <UsersManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Team', action: 'view' }}>
            <Helmet>
              <title>View Team Member</title>
              <meta name="description" content="View team member details and information" />
            </Helmet>
            <ViewUser />
          </ProtectedRoute>
        }
      />

      {/* Roles Management - Requires Role view_all permission */}
      <Route
        path="/roles"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Role', action: 'view_all' }}>
            <Helmet>
              <title>Roles Management</title>
              <meta name="description" content="Manage user roles and permissions" />
            </Helmet>
            <RolesManagement />
          </ProtectedRoute>
        }
      />

      {/* State Management - Requires StateCircle view_all permission */}
      <Route
        path="/states"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'StateCircle', action: 'view_all' }}>
            <Helmet>
              <title>State Management</title>
              <meta name="description" content="Manage states" />
            </Helmet>
            <StateManagement />
          </ProtectedRoute>
        }
      />

      {/* Circle Management - Requires StateCircle view_all permission */}
      <Route
        path="/circles"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'StateCircle', action: 'view_all' }}>
            <Helmet>
              <title>Circle Management</title>
              <meta name="description" content="Manage circles and regions" />
            </Helmet>
            <CircleManagement />
          </ProtectedRoute>
        }
      />

      {/* Activities - Requires Activity view_all permission */}
      <Route
        path="/activities"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Activity', action: 'view_all' }}>
            <Helmet>
              <title>Activities Management</title>
              <meta name="description" content="Manage and track activities" />
            </Helmet>
            <ActivitiesManagement />
          </ProtectedRoute>
        }
      />

      {/* Client Management - Requires Client view_all permission */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Client', action: 'view_all' }}>
            <Helmet>
              <title>Client Management</title>
              <meta name="description" content="Manage clients and their information" />
            </Helmet>
            <ClientManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Client', action: 'view' }}>
            <Helmet>
              <title>View Client</title>
              <meta name="description" content="View client details and information" />
            </Helmet>
            <ClientView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendor"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorManagement', action: 'view_all' }}>
            <Helmet>
              <title>Vendor Management</title>
              <meta name="description" content="Manage vendors and suppliers" />
            </Helmet>
            <VendorManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorManagement', action: 'view' }}>
            <Helmet>
              <title>View Vendor</title>
              <meta name="description" content="View vendor details and information" />
            </Helmet>
            <VendorView />
          </ProtectedRoute>
        }
      />

      {/* Project Management - Requires Project view_all permission */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Project', action: 'view_all' }}>
            <Helmet>
              <title>Project Management</title>
              <meta name="description" content="Manage projects and their details" />
            </Helmet>
            <ProjectManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/view/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Project', action: 'view' }}>
            <Helmet>
              <title>View Project</title>
              <meta name="description" content="View project details and information" />
            </Helmet>
            <ViewProject />
          </ProtectedRoute>
        }
      />

      {/* Designations - Requires Designation view_all permission */}
      <Route
        path="/designations"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Designation', action: 'view_all' }}>
            <Helmet>
              <title>Designations Management</title>
              <meta name="description" content="Manage employee designations and positions" />
            </Helmet>
            <DesignationsManagement />
          </ProtectedRoute>
        }
      />

      {/* Departments - Requires Department view_all permission */}
      <Route
        path="/departments"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Department', action: 'view_all' }}>
            <Helmet>
              <title>Departments Management</title>
              <meta name="description" content="Manage departments and organizational units" />
            </Helmet>
            <DepartmentsManagement />
          </ProtectedRoute>
        }
      />

      {/* Client Projects - Requires ClientProject view_all permission */}
      <Route
        path="/client-projects"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'ClientProject', action: 'view_all' }}>
            <Helmet>
              <title>Client Projects</title>
              <meta name="description" content="Manage client projects and assignments" />
            </Helmet>
            <ClientProjectsManagement />
          </ProtectedRoute>
        }
      />

      {/* Sites Management - Requires Site view_my permission */}
      <Route
        path="/sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'view_my' }}>
            <Helmet>
              <title>Sites Management</title>
              <meta name="description" content="Manage sites and locations" />
            </Helmet>
            <SitesManagement />
          </ProtectedRoute>
        }
      />
      {/* Sites Create - Requires Site create permission */}
      <Route
        path="/sites/create"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'create' }}>
            <Helmet>
              <title>Create Site</title>
              <meta name="description" content="Create a new site" />
            </Helmet>
            <CreateSite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites/:id/update"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'create' }}>
            <Helmet>
              <title>Update Site</title>
              <meta name="description" content="Create a new site" />
            </Helmet>
            <EditSite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites/upload"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'upload' }}>
            <Helmet>
              <title>Bulk Site Upload</title>
              <meta name="description" content="Upload multiple sites at once" />
            </Helmet>
            <BulkSiteUpload />
          </ProtectedRoute>
        }
      />
      {/* Sites Assign - Requires Site assign permission */}
      <Route
        path="/sites/assign"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'assign' }}>
            <Helmet>
              <title>Site Assignment</title>
              <meta name="description" content="Assign sites to team members" />
            </Helmet>
            <SiteAssignment />
          </ProtectedRoute>
        }
      />
      {/* Project Sites - Requires Site view_all permission */}
      <Route
        path="/sites/project-sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'view_all' }}>
            <Helmet>
              <title>Project Sites</title>
              <meta name="description" content="View all project sites" />
            </Helmet>
            <ProjectSitesPage />
          </ProtectedRoute>
        }
      />
      {/* Requested Sites - engineers' requests to work a survey site */}
      <Route
        path="/sites/requested"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'site_requests_view' }}>
            <Helmet>
              <title>Assign Request</title>
              <meta name="description" content="Survey sites field engineers have requested to work" />
            </Helmet>
            <RequestedSitesPage />
          </ProtectedRoute>
        }
      />
      {/* My Sites - visible to any user with any site-level permission */}
      <Route
        path="/sites/my-sites"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'Site', action: 'view_my' },
            { resource: 'Site', action: 'view_all' },
            { resource: 'Site', action: 'assign' },
            { resource: 'Site', action: 're_assign' },
            { resource: 'Site', action: 'upload' },
            { resource: 'Site', action: 'create' },
            { resource: 'Site', action: 'map_view' },
            { resource: 'Site', action: 'mis' },
            { resource: 'Site', action: 'cancel' },
            { resource: 'Site', action: 'hold' },
            { resource: 'Site', action: 'view' },
            { resource: 'Site', action: 'validator' },
            { resource: 'Site', action: 'gallery_upload' },
          ]}>
            <Helmet>
              <title>My Sites</title>
              <meta name="description" content="View sites assigned to me" />
            </Helmet>
            <MySitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/map-view"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'map_view' }}>
            <Helmet>
              <title>Map View</title>
              <meta name="description" content="Plan site routes and filter nearby assignments" />
            </Helmet>
            <MapViewPage />
          </ProtectedRoute>
        }
      />
      {/* Sites View - Requires Site view permission */}
      <Route
        path="/sites/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Site', action: 'view' }}>
            <Helmet>
              <title>View Site</title>
              <meta name="description" content="View site details and information" />
            </Helmet>
            <ViewSite />
          </ProtectedRoute>
        }
      />

      {/* ── Installation & Commissioning (INC) ── */}
      <Route
        path="/inc/dashboard"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'IncSite', action: 'view_all' },
            { resource: 'IncSite', action: 'view' },
          ]}>
            <Helmet>
              <title>INC Dashboard</title>
              <meta name="description" content="Installation & Commissioning dashboard" />
            </Helmet>
            <IncDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inc/sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'IncSite', action: 'view_all' }}>
            <Helmet>
              <title>INC Sites</title>
              <meta name="description" content="View all INC sites" />
            </Helmet>
            <IncSitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inc/my-sites"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'IncSite', action: 'view' },
            { resource: 'IncSite', action: 'view_all' },
            { resource: 'IncSite', action: 'create' },
            { resource: 'IncSite', action: 'edit' },
            { resource: 'IncSite', action: 'assign' },
            { resource: 'IncSite', action: 'upload' },
          ]}>
            <Helmet>
              <title>My INC Sites</title>
              <meta name="description" content="View INC sites assigned to me" />
            </Helmet>
            <MyIncSitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inc/create"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'IncSite', action: 'create' }}>
            <Helmet>
              <title>Create INC Site</title>
              <meta name="description" content="Create a new INC site" />
            </Helmet>
            <CreateIncSite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inc/upload"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'IncSite', action: 'upload' }}>
            <Helmet>
              <title>Import INC Sites</title>
              <meta name="description" content="Bulk import INC sites" />
            </Helmet>
            <IncBulkUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inc/:id/update"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'IncSite', action: 'edit' }}>
            <Helmet>
              <title>Edit INC Site</title>
              <meta name="description" content="Edit an INC site" />
            </Helmet>
            <EditIncSite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inc/sites/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'IncSite', action: 'view' }}>
            <Helmet>
              <title>View INC Site</title>
              <meta name="description" content="View INC site details" />
            </Helmet>
            <ViewIncSite />
          </ProtectedRoute>
        }
      />

      {/* ── Designing (report preparation only) — one DesignSite permission per route ── */}
      <Route
        path="/designing/sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'DesignSite', action: 'view_all' }}>
            <Helmet>
              <title>Designing - Project Sites</title>
              <meta name="description" content="Designing sites of the projects assigned to you" />
            </Helmet>
            <DesignSitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/designing/my-sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'DesignSite', action: 'view_my' }}>
            <Helmet>
              <title>Designing - My Sites</title>
              <meta name="description" content="Designing sites you created, or are the report maker or validator of" />
            </Helmet>
            <MyDesignSitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/designing/create"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'DesignSite', action: 'create' }}>
            <Helmet>
              <title>Create Designing Site</title>
              <meta name="description" content="Create a new Designing site" />
            </Helmet>
            <CreateDesignSite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/designing/upload"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'DesignSite', action: 'upload' }}>
            <Helmet>
              <title>Import Designing Sites</title>
              <meta name="description" content="Bulk import Designing sites" />
            </Helmet>
            <DesignBulkUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/designing/:id/update"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'DesignSite', action: 'edit' }}>
            <Helmet>
              <title>Edit Designing Site</title>
              <meta name="description" content="Edit a Designing site" />
            </Helmet>
            <EditDesignSite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/designing/sites/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'DesignSite', action: 'view' }}>
            <Helmet>
              <title>View Designing Site</title>
              <meta name="description" content="View Designing site details" />
            </Helmet>
            <DesignSiteView />
          </ProtectedRoute>
        }
      />

      {/* Project Claims - Requires project_claim view_all permission */}
      <Route
        path="/claims/project"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'project_claim', action: 'view_all' }}>
            <Helmet>
              <title>Project Claims</title>
              <meta name="description" content="Manage project expense claims" />
            </Helmet>
            <ProjectClaim />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims/project/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'project_claim', action: 'view' }}>
            <Helmet>
              <title>View Project Claim</title>
              <meta name="description" content="View project claim details" />
            </Helmet>
            <ProjectClaimView />
          </ProtectedRoute>
        }
      />

      {/* General Claims - Requires general_claim view_all permission */}
      <Route
        path="/claims/general"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'general_claim', action: 'view_all' }}>
            <Helmet>
              <title>General Claims</title>
              <meta name="description" content="Manage general expense claims" />
            </Helmet>
            <GeneralClaim />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims/general/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'general_claim', action: 'view' }}>
            <Helmet>
              <title>View General Claim</title>
              <meta name="description" content="View general claim details" />
            </Helmet>
            <GeneralClaimView />
          </ProtectedRoute>
        }
      />

      {/* Advance Claims - Requires advance_claim view_all permission */}
      <Route
        path="/claims/advance"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'advance_claim', action: 'view_all' },
            { resource: 'advance_claim', action: 'submit' },
            { resource: 'advance_claim', action: 'approve' }
          ]}>
            <Helmet>
              <title>Advance Claims</title>
              <meta name="description" content="Manage advance payment claims" />
            </Helmet>
            <AdvancesClaim />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims/advance/:id"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'advance_claim', action: 'view' },
            { resource: 'advance_claim', action: 'approve' }
          ]}>
            <Helmet>
              <title>View Advance Claim</title>
              <meta name="description" content="View advance claim details" />
            </Helmet>
            <AdvanceView />
          </ProtectedRoute>
        }
      />

      {/* Advance Restrictions - Admin only */}
      <Route
        path="/claims/advance/restrictions"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'advance_claim', action: 'approve' }}>
            <Helmet>
              <title>Advance Restrictions</title>
              <meta name="description" content="Manage advance claim restrictions and limits" />
            </Helmet>
            <AdvanceRestrictions />
          </ProtectedRoute>
        }
      />

      {/* Supplier Payment Requests */}
      <Route
        path="/claims/supplier-payment"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'supplier_payment', action: 'view_all' },
            { resource: 'supplier_payment', action: 'view_own' },
            { resource: 'supplier_payment', action: 'create' },
            { resource: 'supplier_payment', action: 'approve' }
          ]}>
            <Helmet>
              <title>Supplier Payment Requests</title>
              <meta name="description" content="Manage supplier payment requests" />
            </Helmet>
            <SupplierPaymentPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/claims/supplier-payment/restrictions"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'supplier_payment', action: 'manage_restrictions' }}>
            <Helmet>
              <title>Supplier Payment Restrictions</title>
              <meta name="description" content="Manage supplier payment restrictions and limits" />
            </Helmet>
            <SupplierPaymentRestrictions />
          </ProtectedRoute>
        }
      />

      <Route
        path="/claims/supplier-payment/:id"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'supplier_payment', action: 'view_all' },
            { resource: 'supplier_payment', action: 'view_own' },
            { resource: 'supplier_payment', action: 'approve' }
          ]}>
            <Helmet>
              <title>View Supplier Payment</title>
              <meta name="description" content="View supplier payment request details" />
            </Helmet>
            <SupplierPaymentView />
          </ProtectedRoute>
        }
      />

      {/* Cab Hiring Claims - Requires cab_claim read_all permission */}
      <Route
        path="/claims/cab-hiring"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'cab_claim', action: 'read_all' }}>
            <Helmet>
              <title>Cab Hiring Claims</title>
              <meta name="description" content="Manage cab hiring expense claims" />
            </Helmet>
            <CabHiringClaim />
          </ProtectedRoute>
        }
      />
      <Route
        path="/claims/cab-hiring/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'cab_claim', action: 'read' }}>
            <Helmet>
              <title>View Cab Hiring Claim</title>
              <meta name="description" content="View cab hiring claim details" />
            </Helmet>
            <CabHiringView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaves/dashboard"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'leave', action: 'view_dashboard' }}>
            <Helmet>
              <title>Leave Dashboard</title>
              <meta name="description" content="Leave used and balance per employee" />
            </Helmet>
            <LeaveDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaves"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'leave', action: 'view_all' }}>
            <Helmet>
              <title>Leave Management</title>
              <meta name="description" content="Manage employee leaves and time off" />
            </Helmet>
            <LeaveManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'calendar', action: 'view_all' }}>
            <Helmet>
              <title>Holiday Calendar</title>
              <meta name="description" content="View and manage holiday calendar" />
            </Helmet>
            <HolidayManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            <AttendanceRouteRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/my"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'attendance', action: 'view_my' }}>
            <Helmet>
              <title>My Attendances</title>
              <meta name="description" content="View your attendance records" />
            </Helmet>
            <MyAttendancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/team"
        element={
          <ProtectedRoute
            requiredAnyPermissions={[
              { resource: 'attendance', action: 'view_team' },
              { resource: 'attendance', action: 'view_all' }
            ]}
          >
            <Helmet>
              <title>Team Attendance</title>
              <meta name="description" content="Track and manage team attendance" />
            </Helmet>
            <AttendanceList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/project-team"
        element={
          <ProtectedRoute
            requiredAnyPermissions={[
              { resource: 'attendance', action: 'view_project_team' },
              { resource: 'attendance', action: 'view_all' }
            ]}
          >
            <Helmet>
              <title>Project Team Attendance</title>
              <meta name="description" content="Track project team attendance records" />
            </Helmet>
            <ProjectTeamAttendancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/project-team-members"
        element={
          <ProtectedRoute
            requiredAnyPermissions={[
              { resource: 'attendance', action: 'view_project_team' },
              { resource: 'attendance', action: 'view_all' }
            ]}
          >
            <Helmet>
              <title>Team Member</title>
              <meta name="description" content="View team members" />
            </Helmet>
            <ProjectTeamMembersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/verifications"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'attendance', action: 'view_verifications' }}>
            <Helmet>
              <title>Attendance Verifications</title>
              <meta name="description" content="Confirm attendance your team marked for days that were never marked" />
            </Helmet>
            <AttendanceVerifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/manual-mark-settings"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'attendance', action: 'manage_manual_mark_settings' }}>
            <Helmet>
              <title>Manual Attendance Settings</title>
              <meta name="description" content="Allow or disable manual attendance for today's date" />
            </Helmet>
            <ManualMarkSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/attendance-verifications"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Human Resource', action: 'attendance_verifications' }}>
            <Helmet>
              <title>HR Attendance Verifications</title>
              <meta name="description" content="Verify attendance for employees in your projects" />
            </Helmet>
            <AttendanceVerifications projectScoped />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/leaves"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Human Resource', action: 'leave_view' }}>
            <Helmet>
              <title>HR Leave View</title>
              <meta name="description" content="View leave applications of employees in your projects" />
            </Helmet>
            <LeaveManagement projectScoped />
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance/work-schedules"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'attendance_work_schedule', action: 'view_all' }}>
            <Helmet>
              <title>Working Schedules</title>
              <meta name="description" content="Manage user weekly working schedules" />
            </Helmet>
            <AttendanceWorkSchedules />
          </ProtectedRoute>
        }
      />
        <Route
          path="/attendance/all-users"
          element={
            <ProtectedRoute
              requiredPermission={{ resource: 'Human Resource', action: 'user_attendance' }}
            >
            <Helmet>
              <title>User Attendance</title>
              <meta name="description" content="View all users attendance across projects" />
            </Helmet>
            <AllUsersAttendancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Billing', action: 'view_stats' }}>
            <Helmet>
              <title>Billings Management</title>
              <meta name="description" content="Manage billing and invoices" />
            </Helmet>
            <BillingsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Billing', action: 'view_stats' }}>
            <Helmet>
              <title>View Billing</title>
              <meta name="description" content="View billing details and information" />
            </Helmet>
            <ViewBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/pending-sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Billing', action: 'view_eligible_sites' }}>
            <Helmet>
              <title>Pending Billing Sites</title>
              <meta name="description" content="View sites with pending billing" />
            </Helmet>
            <BillingSites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/add/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'Billing', action: 'add' }}>
            <Helmet>
              <title>Add Billing</title>
              <meta name="description" content="Create a new billing entry" />
            </Helmet>
            <AddBillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/vendor-work-acceptance"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorInvoicing', action: 'view_vendor_work_acceptance' }}>
            <Helmet><title>Vendor Work Acceptance</title></Helmet>
            <VendorWorkAcceptance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/vendor-work-acceptance/field-work"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorInvoicing', action: 'view_vendor_work_acceptance' }}>
            <Helmet><title>Field Work Sites</title></Helmet>
            <VendorWorkAcceptance mode="field_work" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/vendor-work-acceptance/report-prep"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorInvoicing', action: 'view_vendor_work_acceptance' }}>
            <Helmet><title>Report Prep Sites</title></Helmet>
            <VendorWorkAcceptance mode="report_prep" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/vendor-work-acceptance/complete"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorInvoicing', action: 'view_vendor_work_acceptance' }}>
            <Helmet><title>Complete Sites</title></Helmet>
            <VendorWorkAcceptance mode="complete" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/vendor-invoice-acceptance"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorInvoicing', action: 'accept_vendor_invoice' }}>
            <Helmet><title>Vendor Invoice Acceptance</title></Helmet>
            <VendorInvoiceAcceptance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings/po-issued"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorInvoicing', action: 'view_po_issued' }}>
            <Helmet><title>PO Issued</title></Helmet>
            <POIssuedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-invoice-items"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorInvoiceItem', action: 'view_all' }}>
            <Helmet><title>Vendor Invoice Items</title></Helmet>
            <VendorInvoiceItemsPage />
          </ProtectedRoute>
        }
      />

      {/* Public slideshow - no auth, no layout */}
      <Route path="/slideshow/:slug" element={<SlideshowPage />} />

      {/* Training Management */}
      <Route
        path="/training/requests"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Training Requests</title>
              <meta name="description" content="Manage training requests" />
            </Helmet>
            <TrainingRequestsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/institutes"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Training Institutes</title>
              <meta name="description" content="Manage training institutes" />
            </Helmet>
            <TrainingInstitutesManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/courses"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Courses</title>
              <meta name="description" content="Manage training courses" />
            </Helmet>
            <TrainingTypesManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/payments"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Training Payments</title>
              <meta name="description" content="Manage training payments" />
            </Helmet>
            <TrainingPaymentsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/certifications"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Training Certifications</title>
              <meta name="description" content="Manage training certifications" />
            </Helmet>
            <TrainingCertificationsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/requests/:id"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Training Request</title>
              <meta name="description" content="View training request workflow" />
            </Helmet>
            <TrainingRequestView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>My Notifications</title>
              <meta name="description" content="View your notifications" />
            </Helmet>
            <NotificationsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tickets/raise"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'ticket', action: 'view_my' }}>
            <Helmet>
              <title>Raise Ticket</title>
              <meta name="description" content="Raise and follow up on support tickets" />
            </Helmet>
            <RaiseTicketPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets/actions"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'ticket', action: 'view_all' }}>
            <Helmet>
              <title>Ticket Actions</title>
              <meta name="description" content="Manage and perform actions on support tickets" />
            </Helmet>
            <TicketActionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/user-details"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'user_details', action: 'view_all' }}>
            <Helmet>
              <title>HR User Details</title>
              <meta name="description" content="Manage HR user extended details" />
            </Helmet>
            <HrUserDetailsPage />
          </ProtectedRoute>
        }
      />
        <Route
          path="/hr/user-registration"
          element={
            <ProtectedRoute requiredPermission={{ resource: 'Human Resource', action: 'user_registration' }}>
            <Helmet>
              <title>User Registration</title>
              <meta name="description" content="View users registered under your projects" />
            </Helmet>
            <UserRegistrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/manage"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Notification Management</title>
              <meta name="description" content="Manage system notifications" />
            </Helmet>
            <NotificationManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Templates Management</title>
              <meta name="description" content="Manage notification templates" />
            </Helmet>
            <TemplateManagement />
          </ProtectedRoute>
        }
      />


      {/* Claim Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute requiredAnyPermissions={REPORT_PERMISSIONS}>
            <Helmet>
              <title>Reports</title>
              <meta name="description" content="Reports section" />
            </Helmet>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/claims/project"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_project_claim_report' }}>
            <Helmet>
              <title>Project Claim Report</title>
              <meta name="description" content="Project claim report with location trail and distances" />
            </Helmet>
            <ProjectClaimReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/claims/cab-hiring"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_cab_hiring_claim_report' }}>
            <Helmet>
              <title>Cab Hiring Claim Report</title>
              <meta name="description" content="Cab hiring claim report with location trail and distances" />
            </Helmet>
            <CabHiringClaimReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/claims/general"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_general_claim_report' }}>
            <Helmet>
              <title>General Claim Report</title>
              <meta name="description" content="General claim report" />
            </Helmet>
            <GeneralClaimReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/claims/advance"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_advance_claim_report' }}>
            <Helmet>
              <title>Advance Claim Report</title>
              <meta name="description" content="Advance claim report" />
            </Helmet>
            <AdvanceClaimReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/site"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_site_report' }}>
            <Helmet>
              <title>Site Report</title>
              <meta name="description" content="Site report with workflow status and filters" />
            </Helmet>
            <SiteReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/site-distance"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_site_distance_report' }}>
            <Helmet>
              <title>Site Distance Report (With KM)</title>
              <meta name="description" content="Site distance report by site visit date" />
            </Helmet>
            <SiteDistanceReport />
             </ProtectedRoute>
        }
      />
      <Route
        path="/reports/site-distance-without-km"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_site_distance_report_without_km' }}>
            <Helmet>
              <title>Site Distance Report 2 (Without KM)</title>
              <meta name="description" content="Site distance report without road distances or System TA slabs" />
            </Helmet>
            <SiteDistanceReport variant="without_km" />
          </ProtectedRoute>
        }
      />
        <Route
        path="/reports/users"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_users_report' }}>
            <Helmet>
              <title>Users Report</title>
              <meta name="description" content="Users report with project assignments and designations" />
            </Helmet>
            <UsersReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/daily-travel"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_daily_travel_report' }}>
            <Helmet>
              <title>Daily Travel Report</title>
              <meta name="description" content="Daily travel report with check-in, site sequence, and checkout distances" />
            </Helmet>
            <DailyTravelReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/user-feedback"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_site_report' }}>
            <Helmet>
              <title>User Feedback Report</title>
              <meta name="description" content="User feedback report for TA, MIS, designer and validator remarks" />
            </Helmet>
            <UserFeedbackReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/survey-workflow"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_survey_workflow_report' }}>
            <Helmet>
              <title>Survey Workflow Report</title>
              <meta name="description" content="Stage-wise survey workflow report with full site, field work and review detail" />
            </Helmet>
            <SurveyWorkflowReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/client-view"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_client_view_report' }}>
            <Helmet>
              <title>Client View</title>
              <meta name="description" content="All sites of your projects with field data download and slideshow" />
            </Helmet>
            <ClientViewReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/site-location"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_site_location_report' }}>
            <Helmet>
              <title>Wrong Site Location</title>
              <meta name="description" content="Engineers' reports that a survey site's latitude/longitude is wrong" />
            </Helmet>
            <SiteLocationReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/site-fw-start"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_site_fw_start_report' }}>
            <Helmet>
              <title>Site &amp; FW Start Difference</title>
              <meta name="description" content="Aerial distance between a site and where its field work was started" />
            </Helmet>
            <SiteFwStartReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/reporting-checkin"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_reporting_checkin_report' }}>
            <Helmet>
              <title>Reporting &amp; Attendance Check-in Difference</title>
              <meta name="description" content="Aerial distance between an employee's reporting location and their attendance check-in" />
            </Helmet>
            <ReportingCheckinReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/user-productivity"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_user_productivity_report' }}>
            <Helmet>
              <title>User Productivity Report</title>
              <meta name="description" content="Work assigned to and completed by each user in a month, across all roles" />
            </Helmet>
            <UserProductivityReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/login"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_login_report' }}>
            <Helmet>
              <title>Login Report</title>
              <meta name="description" content="User login and logout activity report with session details" />
            </Helmet>
            <LoginReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/password"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_password_report' }}>
            <Helmet>
              <title>Password Report</title>
              <meta name="description" content="Last password change details for all users" />
            </Helmet>
            <PasswordReport />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/permissions"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'view_permission_report' }}>
            <Helmet>
              <title>Permission Report</title>
              <meta name="description" content="Role permission matrix report" />
            </Helmet>
            <PermissionReportPage />
          </ProtectedRoute>
        }
      />

      {/* App Version Management */}
      <Route
        path="/app-versions"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'AppVersion', action: 'view_all' }}>
            <Helmet>
              <title>App Version Management</title>
              <meta name="description" content="Manage mobile app versions and releases" />
            </Helmet>
            <AppVersionManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/masters"
        element={
          <ProtectedRoute>
            <Helmet>
              <title>Masters</title>
            </Helmet>
            <MastersPage />
          </ProtectedRoute>
        }
      />

      {/* Vendor Portal */}
      <Route
        path="/vendor-portal/sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_sites' }}>
            <Helmet><title>Vendor Portal – Sites</title></Helmet>
            <VendorPortalSites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/sites/:id"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_sites' }}>
            <Helmet><title>Vendor Portal – Site Detail</title></Helmet>
            <VendorPortalSiteView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/team"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'manage_team' }}>
            <Helmet><title>Vendor Portal – Team</title></Helmet>
            <VendorPortalTeam />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/billings"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_billing' }}>
            <Helmet><title>Vendor Portal – Billing</title></Helmet>
            <VendorPortalBillings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/billings/eligible"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_billing' }}>
            <Helmet><title>Vendor Portal – File Billing</title></Helmet>
            <VendorPortalEligibleBillingSites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/field-work-sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_sites' }}>
            <Helmet><title>Vendor Portal – Field Work Sites</title></Helmet>
            <VendorPortalFieldWorkSites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/report-sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_sites' }}>
            <Helmet><title>Vendor Portal – Report Sites</title></Helmet>
            <VendorPortalReportSites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/complete-sites"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_sites' }}>
            <Helmet><title>Vendor Portal – Complete Sites</title></Helmet>
            <VendorPortalCompleteSites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/invoices"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_invoices' }}>
            <Helmet><title>Vendor Portal – Purchase Orders</title></Helmet>
            <VendorPortalInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/site-invoices/field-work"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_invoices' }}>
            <VendorPortalSiteInvoices group="field_work" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/site-invoices/report-preparation"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_invoices' }}>
            <VendorPortalSiteInvoices group="report_prep" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/site-invoices/complete"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_invoices' }}>
            <VendorPortalSiteInvoices group="complete" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-portal/submitted-invoices"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'VendorPortal', action: 'view_invoices' }}>
            <Helmet><title>Vendor Portal – Submitted Invoices</title></Helmet>
            <VendorPortalSubmittedInvoices />
          </ProtectedRoute>
        }
      />

      {/* Requirements Management */}
      <Route
        path="/requirements"
        element={
          <ProtectedRoute requiredAnyPermissions={[
            { resource: 'OperationsRequirementsProjectActivity', action: 'view_project_specific' },
            { resource: 'OperationsRequirementsProjectActivity', action: 'view_activity_wise' },
            { resource: 'OperationsRequirementsProjectActivity', action: 'view_common_all' }
          ]}>
            <Helmet><title>Project Requirements</title></Helmet>
            <RequirementsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requirements/project-specific"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'OperationsRequirementsProjectActivity', action: 'view_project_specific' }}>
            <Helmet><title>Project Specific Requirements</title></Helmet>
            <ProjectSpecificPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requirements/activity-wide"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'OperationsRequirementsProjectActivity', action: 'view_activity_wise' }}>
            <Helmet><title>Activity Wise Requirements</title></Helmet>
            <ActivityWidePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requirements/common"
        element={
          <ProtectedRoute requiredPermission={{ resource: 'OperationsRequirementsProjectActivity', action: 'view_common_all' }}>
            <Helmet><title>Common Requirements</title></Helmet>
            <CommonRequirementsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all route - redirect to dashboard or unauthorized */}
      <Route
        path="*"
        element={token ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />}
      />
    </Routes>
  );
};

const AppWrapper = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <NotificationProvider>
          {/* Every confirm / prompt in the app renders through this provider —
              window.confirm and window.prompt are not used anywhere. */}
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
        </NotificationProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default AppWrapper;
